import { userManagementApi } from './userManagementService';

// Tipuri pentru setari master
export interface MasterSettings {
  id: string;
  systemName: string;
  systemVersion: string;
  environment: 'development' | 'staging' | 'production';
  maintenanceMode: boolean;
  maintenanceMessage?: string;
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru branding
export interface BrandingSettings {
  id: string;
  logo?: string;
  favicon?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string;
  customCSS?: string;
  customJavaScript?: string;
  theme: 'light' | 'dark' | 'auto';
  layout: 'default' | 'compact' | 'spacious';
  sidebarCollapsed: boolean;
  headerStyle: 'default' | 'minimal' | 'extended';
  footerText?: string;
  footerLinks?: {
    label: string;
    url: string;
    target: '_blank' | '_self';
  }[];
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru integrari
export interface IntegrationSettings {
  id: string;
  name: string;
  type: 'payment' | 'email' | 'sms' | 'analytics' | 'storage' | 'backup' | 'monitoring' | 'other';
  provider: string;
  isActive: boolean;
  config: {
    apiKey?: string;
    apiSecret?: string;
    webhookUrl?: string;
    endpoint?: string;
    timeout?: number;
    retryAttempts?: number;
    [key: string]: any;
  };
  metadata: {
    version?: string;
    description?: string;
    documentation?: string;
    supportEmail?: string;
    [key: string]: any;
  };
  lastSync?: string;
  syncStatus: 'success' | 'error' | 'pending' | 'disabled';
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru setari de securitate
export interface SecuritySettings {
  id: string;
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
    maxAge: number; // days
    preventReuse: number; // last N passwords
  };
  sessionSettings: {
    timeout: number; // minutes
    maxConcurrent: number;
    requireReauth: boolean;
    rememberMe: boolean;
  };
  twoFactorAuth: {
    enabled: boolean;
    required: boolean;
    providers: ('email' | 'sms' | 'totp' | 'backup')[];
  };
  ipWhitelist: {
    enabled: boolean;
    ips: string[];
  };
  rateLimiting: {
    enabled: boolean;
    requestsPerMinute: number;
    requestsPerHour: number;
    requestsPerDay: number;
  };
  auditLogging: {
    enabled: boolean;
    retentionDays: number;
    logLevels: ('info' | 'warning' | 'error' | 'critical')[];
  };
  dataEncryption: {
    enabled: boolean;
    algorithm: string;
    keyRotation: number; // days
  };
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru setari de notificari sistem
export interface SystemNotificationSettings {
  id: string;
  email: {
    enabled: boolean;
    smtp: {
      host: string;
      port: number;
      secure: boolean;
      username: string;
      password: string;
    };
    from: {
      name: string;
      email: string;
    };
    templates: {
      welcome: string;
      passwordReset: string;
      systemAlert: string;
      maintenance: string;
    };
  };
  sms: {
    enabled: boolean;
    provider: string;
    apiKey: string;
    apiSecret: string;
    from: string;
  };
  push: {
    enabled: boolean;
    firebase: {
      serverKey: string;
      projectId: string;
    };
  };
  webhooks: {
    enabled: boolean;
    endpoints: {
      url: string;
      events: string[];
      secret?: string;
      isActive: boolean;
    }[];
  };
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru setari de backup
export interface BackupSettings {
  id: string;
  enabled: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string; // HH:MM format
  retention: {
    daily: number; // days
    weekly: number; // weeks
    monthly: number; // months
  };
  storage: {
    type: 'local' | 's3' | 'gcs' | 'azure';
    config: {
      bucket?: string;
      region?: string;
      accessKey?: string;
      secretKey?: string;
      endpoint?: string;
      [key: string]: any;
    };
  };
  compression: boolean;
  encryption: boolean;
  lastBackup?: string;
  nextBackup?: string;
  status: 'success' | 'error' | 'pending' | 'disabled';
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru setari de monitorizare
export interface MonitoringSettings {
  id: string;
  enabled: boolean;
  metrics: {
    system: boolean;
    application: boolean;
    database: boolean;
    network: boolean;
    custom: boolean;
  };
  alerts: {
    cpu: {
      enabled: boolean;
      threshold: number; // percentage
    };
    memory: {
      enabled: boolean;
      threshold: number; // percentage
    };
    disk: {
      enabled: boolean;
      threshold: number; // percentage
    };
    responseTime: {
      enabled: boolean;
      threshold: number; // milliseconds
    };
    errorRate: {
      enabled: boolean;
      threshold: number; // percentage
    };
  };
  reporting: {
    enabled: boolean;
    frequency: 'daily' | 'weekly' | 'monthly';
    recipients: string[];
    format: 'pdf' | 'html' | 'csv';
  };
  integrations: {
    slack?: {
      enabled: boolean;
      webhook: string;
      channel: string;
    };
    email?: {
      enabled: boolean;
      recipients: string[];
    };
    pagerduty?: {
      enabled: boolean;
      apiKey: string;
      serviceKey: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru setari de API
export interface ApiSettings {
  id: string;
  versioning: {
    enabled: boolean;
    currentVersion: string;
    supportedVersions: string[];
    deprecationPolicy: {
      enabled: boolean;
      noticePeriod: number; // days
    };
  };
  rateLimiting: {
    enabled: boolean;
    global: {
      requestsPerMinute: number;
      requestsPerHour: number;
      requestsPerDay: number;
    };
    perUser: {
      requestsPerMinute: number;
      requestsPerHour: number;
      requestsPerDay: number;
    };
  };
  authentication: {
    methods: ('api_key' | 'oauth2' | 'jwt' | 'basic')[];
    jwt: {
      secret: string;
      expiresIn: string;
      refreshExpiresIn: string;
    };
    oauth2: {
      enabled: boolean;
      providers: {
        google?: {
          clientId: string;
          clientSecret: string;
        };
        microsoft?: {
          clientId: string;
          clientSecret: string;
        };
        github?: {
          clientId: string;
          clientSecret: string;
        };
      };
    };
  };
  cors: {
    enabled: boolean;
    origins: string[];
    methods: string[];
    headers: string[];
    credentials: boolean;
  };
  logging: {
    enabled: boolean;
    level: 'debug' | 'info' | 'warn' | 'error';
    format: 'json' | 'text';
    includeBody: boolean;
    includeHeaders: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

// Tipuri pentru filtre
export interface MasterSettingsFilters {
  type?: string;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

class MasterSettingsService {
  private baseUrl = '/master-settings';

  // Setari master
  async getMasterSettings(): Promise<MasterSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching master settings:', error);
      throw error;
    }
  }

  async updateMasterSettings(settings: Partial<MasterSettings>): Promise<MasterSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating master settings:', error);
      throw error;
    }
  }

  // Branding
  async getBrandingSettings(): Promise<BrandingSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/branding`);
      return response.data;
    } catch (error) {
      console.error('Error fetching branding settings:', error);
      throw error;
    }
  }

  async updateBrandingSettings(settings: Partial<BrandingSettings>): Promise<BrandingSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/branding`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating branding settings:', error);
      throw error;
    }
  }

  async uploadLogo(file: File): Promise<{ url: string; }> {
    try {
      const formData = new FormData();
      formData.append('logo', file);
      
      const response = await userManagementApi.post(`${this.baseUrl}/branding/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  }

  async uploadFavicon(file: File): Promise<{ url: string; }> {
    try {
      const formData = new FormData();
      formData.append('favicon', file);
      
      const response = await userManagementApi.post(`${this.baseUrl}/branding/favicon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading favicon:', error);
      throw error;
    }
  }

  // Integrari
  async getIntegrations(filters?: MasterSettingsFilters): Promise<{ integrations: IntegrationSettings[]; total: number; }> {
    try {
      const params = new URLSearchParams();
      if (filters?.type) params.append('type', filters.type);
      if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await userManagementApi.get(`${this.baseUrl}/integrations?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching integrations:', error);
      throw error;
    }
  }

  async getIntegrationById(id: string): Promise<IntegrationSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/integrations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching integration:', error);
      throw error;
    }
  }

  async createIntegration(integration: Omit<IntegrationSettings, 'id' | 'createdAt' | 'updatedAt'>): Promise<IntegrationSettings> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/integrations`, integration);
      return response.data;
    } catch (error) {
      console.error('Error creating integration:', error);
      throw error;
    }
  }

  async updateIntegration(id: string, integration: Partial<IntegrationSettings>): Promise<IntegrationSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/integrations/${id}`, integration);
      return response.data;
    } catch (error) {
      console.error('Error updating integration:', error);
      throw error;
    }
  }

  async deleteIntegration(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/integrations/${id}`);
    } catch (error) {
      console.error('Error deleting integration:', error);
      throw error;
    }
  }

  async testIntegration(id: string): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/integrations/${id}/test`);
      return response.data;
    } catch (error) {
      console.error('Error testing integration:', error);
      throw error;
    }
  }

  async syncIntegration(id: string): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/integrations/${id}/sync`);
      return response.data;
    } catch (error) {
      console.error('Error syncing integration:', error);
      throw error;
    }
  }

  // Securitate
  async getSecuritySettings(): Promise<SecuritySettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/security`);
      return response.data;
    } catch (error) {
      console.error('Error fetching security settings:', error);
      throw error;
    }
  }

  async updateSecuritySettings(settings: Partial<SecuritySettings>): Promise<SecuritySettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/security`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating security settings:', error);
      throw error;
    }
  }

  // Notificari sistem
  async getSystemNotificationSettings(): Promise<SystemNotificationSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/notifications`);
      return response.data;
    } catch (error) {
      console.error('Error fetching system notification settings:', error);
      throw error;
    }
  }

  async updateSystemNotificationSettings(settings: Partial<SystemNotificationSettings>): Promise<SystemNotificationSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/notifications`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating system notification settings:', error);
      throw error;
    }
  }

  async testEmailSettings(): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/notifications/test-email`);
      return response.data;
    } catch (error) {
      console.error('Error testing email settings:', error);
      throw error;
    }
  }

  async testSmsSettings(): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/notifications/test-sms`);
      return response.data;
    } catch (error) {
      console.error('Error testing SMS settings:', error);
      throw error;
    }
  }

  // Backup
  async getBackupSettings(): Promise<BackupSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/backup`);
      return response.data;
    } catch (error) {
      console.error('Error fetching backup settings:', error);
      throw error;
    }
  }

  async updateBackupSettings(settings: Partial<BackupSettings>): Promise<BackupSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/backup`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating backup settings:', error);
      throw error;
    }
  }

  async createBackup(): Promise<{ success: boolean; message: string; backupId?: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/backup/create`);
      return response.data;
    } catch (error) {
      console.error('Error creating backup:', error);
      throw error;
    }
  }

  async restoreBackup(backupId: string): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/backup/restore`, { backupId });
      return response.data;
    } catch (error) {
      console.error('Error restoring backup:', error);
      throw error;
    }
  }

  // Monitorizare
  async getMonitoringSettings(): Promise<MonitoringSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/monitoring`);
      return response.data;
    } catch (error) {
      console.error('Error fetching monitoring settings:', error);
      throw error;
    }
  }

  async updateMonitoringSettings(settings: Partial<MonitoringSettings>): Promise<MonitoringSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/monitoring`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating monitoring settings:', error);
      throw error;
    }
  }

  // API
  async getApiSettings(): Promise<ApiSettings> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/api`);
      return response.data;
    } catch (error) {
      console.error('Error fetching API settings:', error);
      throw error;
    }
  }

  async updateApiSettings(settings: Partial<ApiSettings>): Promise<ApiSettings> {
    try {
      const response = await userManagementApi.put(`${this.baseUrl}/api`, settings);
      return response.data;
    } catch (error) {
      console.error('Error updating API settings:', error);
      throw error;
    }
  }

  // Export/Import
  async exportSettings(): Promise<Blob> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/export`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting settings:', error);
      throw error;
    }
  }

  async importSettings(file: File): Promise<{ success: boolean; message: string; }> {
    try {
      const formData = new FormData();
      formData.append('settings', file);
      
      const response = await userManagementApi.post(`${this.baseUrl}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error importing settings:', error);
      throw error;
    }
  }

  // Reset
  async resetToDefaults(): Promise<{ success: boolean; message: string; }> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/reset`);
      return response.data;
    } catch (error) {
      console.error('Error resetting settings:', error);
      throw error;
    }
  }
}

export const masterSettingsService = new MasterSettingsService();
